/** Matches backend MeResponseDto / RoleInfoDto */
export interface RoleInfo {
  readonly id: string;
  readonly key: string;
  readonly description: string;
}

/** Strict shape — backend MeResponseDto now returns populated RoleInfoDto */
export interface AuthUser {
  readonly id: string;
  readonly businessId: string;
  readonly email: string;
  readonly name: string;
  readonly phone?: string;
  readonly role: RoleInfo;
  /** @deprecated legacy flat key — kept for localStorage migration grace, use role.key */
  readonly roleName?: string;
  /** @deprecated legacy alias — kept for grace, use role.description */
  readonly roleDescription?: string;
  readonly emailVerified: boolean;
  /** epoch seconds (backend MeResponseDto) */
  readonly createdAt: number;
  /** epoch seconds (backend MeResponseDto) */
  readonly lastLoginAt?: number;
}

/** @deprecated transitional: role may be string during migration (wire or localStorage) */
export type AuthUserRole = RoleInfo | string;

/** Type guard + helpers — tolerate both shapes for migration grace */
export function isRoleInfo(role: unknown): role is RoleInfo {
  return typeof role === 'object' && role !== null && 'key' in (role as Record<string, unknown>) && 'id' in (role as Record<string, unknown>);
}

export function getRoleKey(user: Pick<AuthUser, 'role' | 'roleName'> | { role: unknown; roleName?: string }): string {
  if (isRoleInfo((user as AuthUser).role)) return (user as AuthUser).role.key;
  if ((user as AuthUser).roleName) return (user as AuthUser).roleName!;
  const r = (user as { role: unknown }).role;
  return typeof r === 'string' ? r : '';
}

export function getRoleDescription(user: Pick<AuthUser, 'role' | 'roleName' | 'roleDescription'> | { role: unknown; roleName?: string; roleDescription?: string }): string {
  if (isRoleInfo((user as AuthUser).role)) return (user as AuthUser).role.description ?? (user as AuthUser).role.key;
  if ((user as AuthUser).roleDescription) return (user as AuthUser).roleDescription!;
  if ((user as AuthUser).roleName) return (user as AuthUser).roleName!;
  const r = (user as { role: unknown }).role;
  return typeof r === 'string' ? r : '';
}

export function getRoleId(user: Pick<AuthUser, 'role'> | { role: unknown }): string {
  if (isRoleInfo((user as AuthUser).role)) return (user as AuthUser).role.id;
  const r = (user as { role: unknown }).role;
  return typeof r === 'string' ? r : '';
}

/** Normalize any wire shape (object or flat) into canonical RoleInfo */
export function normalizeRoleInfo(role: unknown, fallbackKey?: string): RoleInfo {
  if (isRoleInfo(role)) return role as RoleInfo;
  if (typeof role === 'string' && role) {
    const key = fallbackKey ?? role;
    return { id: role, key, description: key };
  }
  // defensive fallback — should not happen
  const k = fallbackKey ?? 'unknown';
  return { id: k, key: k, description: k };
}

/** Normalize raw wire/localStorage AuthUser (flat or object) to strict AuthUser */
export function normalizeAuthUser(raw: unknown): AuthUser {
  const r = raw as Record<string, unknown>;
  const roleRaw = r['role'] as unknown;
  const roleName = r['roleName'] as string | undefined;
  const roleDescription = r['roleDescription'] as string | undefined;
  const role: RoleInfo = isRoleInfo(roleRaw)
    ? (roleRaw as RoleInfo)
    : normalizeRoleInfo(roleRaw, roleName ?? roleDescription);

  // Prefer description from object, fallback to legacy fields
  const description = isRoleInfo(roleRaw)
    ? (roleRaw as RoleInfo).description ?? roleName ?? (typeof roleRaw === 'string' ? roleRaw : role.key)
    : roleDescription ?? roleName ?? role.key;

  const normalizedRole: RoleInfo = { id: role.id, key: role.key, description };

  return {
    id: r['id'] as string,
    businessId: (r['businessId'] as string) ?? '',
    email: r['email'] as string,
    name: r['name'] as string,
    phone: r['phone'] as string | undefined,
    role: normalizedRole,
    emailVerified: r['emailVerified'] as boolean,
    createdAt: r['createdAt'] as number,
    lastLoginAt: r['lastLoginAt'] as number | undefined,
  };
}

/** Matches backend AuthSessionResponseDto */
export interface AuthSessionResponse {
  readonly accessToken: string;
  readonly user: AuthUser;
}

/** Matches backend UpdateMeDto (PATCH /auth/me). D-29: at least one field; D-30: server-managed fields are blocked. */
export interface UpdateMeDto {
  readonly name?: string;
  readonly phone?: string;
}

/** Matches backend ChangePasswordDto (POST /auth/change-password). D-33 rotates refresh cookie. */
export interface ChangePasswordDto {
  readonly currentPassword: string;
  readonly newPassword: string;
}

/** Matches backend RefreshResponseDto */
export interface RefreshResponse {
  readonly accessToken: string;
  readonly expiresIn: number;
}

/** Matches backend ForgotPasswordResponseDto */
export interface ForgotPasswordResponse {
  readonly enviado: true;
  readonly resetToken?: string; // dev-mode only
}

/** Matches backend VerifyAndPasswordResponseDto */
export interface VerifyAndPasswordResponse {
  readonly memberId: string;
  readonly emailVerified: boolean;
}

/** Matches backend ResendVerificationResponseDto */
export interface ResendVerificationResponse {
  readonly otp?: string; // dev-mode only
}

// ─── Progressive Auth (Phase 41) ─────────────────────────────────────────

/** POST /auth/user/identify — backend determines next step (anti-enumeration: always 200) */
export interface IdentifyResponse {
  readonly nextStep: 'PASSWORD' | 'OTP';
  readonly verificationId?: string;
  readonly maskedDestination?: string;
}

/** POST /auth/user/otp/verify — returns verificationToken for password creation */
export interface VerifyOtpResponse {
  readonly verificationToken: string;
}

/** POST /auth/user/otp/resend — returns maskedDestination; otp is dev-mode only */
export interface ResendOtpResponse {
  readonly maskedDestination: string;
  readonly otp?: string;
}

/** POST /auth/user/password — same shape as AuthSessionResponse */
export type CreatePasswordOutput = AuthSessionResponse;

// ─── JWT Payload (Phase 48) ────────────────────────────────────────────────

/** JWT payload decoded from accessToken — canonical `perms` + alias `permisos` */
export type JwtPayload = {
  readonly perms?: string[];
  readonly permisos?: string[];
  readonly exp?: number;
  readonly rol?: string;
  readonly subjectType?: string;
  // Phase 53 — onboarding gating
  readonly businessIds?: string[];
  readonly activeBusinessId?: string | null;
  readonly aud?: string | string[];
  readonly sub?: string;
};
